import axios from "axios";

/**
 * Resolve the backend API base URL.
 *
 * Iter 91B.9 P0 — defensive SSOT to avoid the production bundle ever
 * calling the preview/emergent backend by accident:
 *
 *   1. If the browser is running on the canonical production host
 *      (costcontrolx.app or www.costcontrolx.app), force same-origin
 *      ⇒ API calls go to `https://costcontrolx.app/api/*`. This is
 *      independent of REACT_APP_BACKEND_URL baked at build time.
 *   2. Otherwise (preview / dev / staging), fall back to the build-
 *      time `REACT_APP_BACKEND_URL`.
 *   3. Last-resort default to relative `/api` (same-origin) if no
 *      build-time URL is provided.
 */
function resolveBackendBase() {
  const built = process.env.REACT_APP_BACKEND_URL;
  if (typeof window !== "undefined" && window.location && window.location.host) {
    const host = window.location.host.toLowerCase();
    if (host === "costcontrolx.app" || host === "www.costcontrolx.app") {
      return window.location.origin;   // same-origin in production
    }
  }
  if (built && typeof built === "string" && built.trim()) {
    return built.trim().replace(/\/+$/, "");
  }
  return "";  // → API_BASE = "/api"  (same-origin relative)
}

const BACKEND_URL = resolveBackendBase();
export const API_BASE = `${BACKEND_URL}/api`;
export const TOKEN_KEY = "ccx_access_token";
// Phase 10P — when set, the API client sends this token INSTEAD of the
// regular one. It is the short-lived impersonation JWT minted by
// /api/admin/impersonate/{tenant_id}.
export const IMPERSONATION_TOKEN_KEY = "ccx_impersonation_token";

const api = axios.create({
  baseURL: API_BASE,
  // We use Authorization Bearer (token from localStorage) as the primary auth.
  // withCredentials=false avoids the CORS preflight failure that occurs when
  // the upstream proxy responds with `Access-Control-Allow-Origin: *` and the
  // browser refuses to send credentials. The backend also sets httpOnly cookies
  // on same-origin requests, but they're treated as a bonus, not the source of truth.
  withCredentials: false,
  headers: { "Content-Type": "application/json" },
});

// Attach Authorization header from localStorage if present.
// This makes auth work across preview domains where cross-origin cookies are blocked
// (cookies remain as a fallback for same-origin requests and server-driven refresh).
api.interceptors.request.use((config) => {
  try {
    // Impersonation token takes precedence when set (Phase 10P).
    const imp = localStorage.getItem(IMPERSONATION_TOKEN_KEY);
    const token = imp || localStorage.getItem(TOKEN_KEY);
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (e) {
    // localStorage may be blocked in some environments — silently ignore.
  }
  return config;
});

// Auto-clear token on 401 to avoid sticky stale tokens.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      try {
        // If we're in impersonation mode, drop only the imp token so the user
        // falls back to the original platform identity rather than logging out.
        if (localStorage.getItem(IMPERSONATION_TOKEN_KEY)) {
          localStorage.removeItem(IMPERSONATION_TOKEN_KEY);
        } else {
          localStorage.removeItem(TOKEN_KEY);
        }
      } catch (e) {}
    }
    return Promise.reject(err);
  }
);

export function setStoredToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch (e) {}
}

export function getStoredToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (e) {
    return null;
  }
}

// Phase 10P helpers ─────────────────────────────────────────────────────
export function setImpersonationToken(token) {
  try {
    if (token) localStorage.setItem(IMPERSONATION_TOKEN_KEY, token);
    else localStorage.removeItem(IMPERSONATION_TOKEN_KEY);
  } catch (e) {}
}

export function getImpersonationToken() {
  try {
    return localStorage.getItem(IMPERSONATION_TOKEN_KEY);
  } catch (e) {
    return null;
  }
}

export function isImpersonating() {
  return !!getImpersonationToken();
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => {
        if (e == null) return "";
        if (typeof e === "string") return e;
        if (typeof e.msg === "string") return e.msg;
        if (typeof e.message === "string") return e.message;
        // Pydantic v2 sometimes returns {loc, msg, type, input}. Prefer msg+loc.
        if (e.msg && e.loc) return `${e.msg} (${e.loc.join(".")})`;
        return JSON.stringify(e);
      })
      .filter(Boolean)
      .join(" · ");
  if (typeof detail === "object") {
    // Our business-friendly backend errors look like:
    //   { error: "EMAIL_EXISTS", message: "Esiste già un utente con questa email." }
    // FastAPI validation errors look like:
    //   { msg: "...", type: "..." }
    if (typeof detail.message === "string") return detail.message;
    if (typeof detail.msg === "string") return detail.msg;
    if (typeof detail.detail === "string") return detail.detail;
    if (typeof detail.error === "string" && typeof detail.message !== "string") {
      // Last resort: surface the error code, never "[object Object]"
      return detail.error;
    }
    try {
      return JSON.stringify(detail);
    } catch (_e) {
      return "Errore sconosciuto";
    }
  }
  return String(detail);
}

export default api;
